import { useCallback, useMemo } from 'react';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import {
  buildDepartmentIdCondition,
  getDepartmentScopeKeys,
  resolveVisibleDepartments,
} from '@/lib/department-scope';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useMyScope } from '@/hooks/use-my-scope';
import { useAuthStore } from '@/state/store/auth';
import { getOrganizationRoles } from '@/state/store/auth/use-is-protected';
import { useDepartmentOptions } from './use-lookups';

/**
 * Which departments the signed-in user may see, from `HRM_PERMISSIONS.DEPARTMENT`:
 * `VIEW_ALL_DEPARTMENTS` lists every department; otherwise `VIEW_OWN_DEPARTMENT` lists only the
 * departments whose Head of Department role the user holds in the current organization plus their
 * profile department, and without either nothing (see `src/lib/department-scope.ts`).
 *
 * Every department filter and every department-filtered list uses this, so the rule lives in one
 * place: render `departments` as the dropdown options, and pass the picked id through
 * `getDepartmentIdCondition` when building the query so a scoped user's "all" still means "all of
 * mine" and never "everyone".
 */
export const useDepartmentScope = ({
  teamOnly = false,
}: {
  /**
   * Only the user's own team: the departments whose Head of Department role they hold plus their
   * profile department, whatever their department permissions (Team Requests of Service Requests).
   */
  teamOnly?: boolean;
} = {}) => {
  const holdsViewAll = useHasPermission(HRM_PERMISSIONS.PEOPLE.VIEW_ALL_DEPARTMENTS);
  const holdsViewOwn = useHasPermission(HRM_PERMISSIONS.PEOPLE.VIEW_OWN_DEPARTMENT);
  const canViewAll = holdsViewAll && !teamOnly;
  const canViewOwn = holdsViewOwn || teamOnly;
  const user = useAuthStore((state) => state.user);
  const organizationId = useAuthStore((state) => state.selectedOrgId);
  const roles = useMemo(() => getOrganizationRoles(user, organizationId), [user, organizationId]);
  const { DepartmentId: ownDepartmentId = '' } = useMyScope();
  const { data: allDepartments, isLoading } = useDepartmentOptions();

  const departments = useMemo(
    () =>
      resolveVisibleDepartments({
        departments: allDepartments ?? [],
        canViewAll,
        canViewOwn,
        scopeKeys: getDepartmentScopeKeys(roles),
        ownDepartmentId,
      }),
    [allDepartments, canViewAll, canViewOwn, roles, ownDepartmentId]
  );

  const getDepartmentIdCondition = useCallback(
    (selectedId: string) =>
      buildDepartmentIdCondition({
        selectedId,
        visibleIds: departments.map((department) => department.ItemId),
        canViewAll,
      }),
    [departments, canViewAll]
  );

  return { canViewAll, departments, isLoading, getDepartmentIdCondition };
};
